import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip,
  CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const statusLabel = (val) => {
  if (val === 1) return <Chip label="Accepted" color="success" size="small" />;
  if (val === 2) return <Chip label="Rejected" color="error" size="small" />;
  return <Chip label="Pending" color="warning" size="small" />;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RequestForm11 = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const orgId = localStorage.getItem('orgId') || '';

  const fetchRequests = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE}/jd/form11-requests`, { params: { orgId } });
      if (res.data.success) {
        setRequests(res.data.data || []);
      } else {
        setError('Failed to fetch requests');
      }
    } catch (err) {
      setError('Error loading requests');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAccept = async (id) => {
    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE}/jd/form11-request-action`, {
        id, action: 1, orgId
      });
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchRequests();
      } else {
        setError(res.data.message || 'Failed to accept');
      }
    } catch {
      setError('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const openRejectDialog = (id) => {
    setSelectedId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please enter rejection reason');
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE}/jd/form11-request-action`, {
        id: selectedId, action: 2, rejectReason, orgId
      });
      if (res.data.success) {
        setSuccess(res.data.message);
        setRejectDialogOpen(false);
        fetchRequests();
      } else {
        setError(res.data.message || 'Failed to reject');
      }
    } catch {
      setError('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c' }}>
          Request for Form 11
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchRequests} size="small" variant="outlined">
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Application No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Requested By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Request Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((row, idx) => (
                    <TableRow key={row.Id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.ApplicationNo || 'N/A'}</TableCell>
                      <TableCell>{row.RequestByName || row.RequestBy || 'N/A'}</TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>{row.RequestReason || 'N/A'}</TableCell>
                      <TableCell>{formatDate(row.InsertDate)}</TableCell>
                      <TableCell>{statusLabel(row.IsAccept)}</TableCell>
                      <TableCell>
                        {(row.IsAccept === false || row.IsAccept === 0 || row.IsAccept === null) && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<AcceptIcon />}
                              disabled={submitting}
                              onClick={() => handleAccept(row.Id)}
                            >
                              Accept
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<RejectIcon />}
                              disabled={submitting}
                              onClick={() => openRejectDialog(row.Id)}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                        {row.IsAccept === 2 && row.RejectReason && (
                          <Typography variant="caption" color="error">
                            Reason: {row.RejectReason}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#c62828' }}>Reject Form 11 Request</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            required
            placeholder="Enter reason for rejection"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={submitting}
          >
            {submitting ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestForm11;